/**
 * Mocking @theme/component and @theme/utilities for standalone functionality.
 * In a production environment, ensure these are imported from your actual utility paths.
 */
class Component extends HTMLElement {
  constructor() {
    super();
    this.refs = {};
  }

  connectedCallback() {
    this._initializeRefs();
  }

  _initializeRefs() {
    const refElements = this.querySelectorAll('[data-ref]');
    refElements.forEach((el) => {
      const refName = el.dataset.ref;
      this.refs[refName] = el;
    });

    if (this.requiredRefs) {
      this.requiredRefs.forEach((ref) => {
        if (!this.refs[ref]) {
          console.error(`Missing required ref: ${ref}`);
        }
      });
    }
  }
}

const debounce = (fn, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

const requestIdleCallback = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));

const mediaQueryLarge = window.matchMedia('(min-width: 990px)');
const isMobileBreakpoint = () => !mediaQueryLarge.matches;

/**
 * AccordionCustom
 * A custom web component that enhances the native <details> and <summary> elements
 * with responsive behavior, conditional disabling, and keyboard accessibility.
 */
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');
    if (!(details instanceof HTMLDetailsElement)) {
      throw new Error('AccordionCustom: <details> element not found.');
    }
    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');
    if (!(summary instanceof HTMLElement)) {
      throw new Error('AccordionCustom: <summary> element not found.');
    }
    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  /** @type {AbortController} */
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', (e) => this.#handleKeyDown(e), { signal });
    this.summary.addEventListener('click', (e) => this.#handleClick(e), { signal });
    
    mediaQueryLarge.addEventListener('change', () => this.#handleMediaQueryChange(), { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #handleClick(event) {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
    }
  }

  #handleMediaQueryChange() {
    this.#setDefaultOpenState();
  }

  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    const shouldBeOpen = (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
                         (!isMobile && this.hasAttribute('open-by-default-on-desktop'));

    this.details.open = shouldBeOpen;
  }

  #handleKeyDown(event) {
    if (event.key === 'Escape' && this.#closeWithEscape && this.details.open) {
      event.preventDefault();
      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}

/**
 * A custom element that manages the account login actions.
 */
class AccountLoginActions extends HTMLElement {
  shopLoginButton = null;

  connectedCallback() {
    this.shopLoginButton = this.querySelector('shop-login-button');

    if (this.shopLoginButton) {
      this.shopLoginButton.setAttribute('full-width', 'true');
      this.shopLoginButton.setAttribute('persist-after-sign-in', 'true');
      this.shopLoginButton.setAttribute('analytics-context', 'loginWithShopSelfServe');
      this.shopLoginButton.setAttribute('flow-version', 'account-actions-popover');
      this.shopLoginButton.setAttribute('return-uri', window.location.href);

      this.shopLoginButton.addEventListener('completed', () => {
        window.location.reload();
      });
    }
  }
}

if (!customElements.get('account-login-actions')) {
  customElements.define('account-login-actions', AccountLoginActions);
}

/**
 * A custom element that manages the popover + popover trigger relationship for anchoring.
 * Calculates the trigger position and inlines custom properties on the popover element
 * that can be consumed by CSS for positioning.
 */
export class AnchoredPopoverComponent extends Component {
  requiredRefs = ['popover', 'trigger'];
  interaction_delay = 200;
  #popoverTrigger = /** @type {number | null} */ (null);

  connectedCallback() {
    super.connectedCallback();
    const { popover, trigger } = this.refs;

    if (!popover || !trigger) return;

    // Listen for native popover toggle events
    popover.addEventListener('beforetoggle', (e) => this.#onPopoverToggle(e));

    // Optional hover behavior
    if (this.hasAttribute('data-hover-triggered')) {
      trigger.addEventListener('mouseenter', this.#onTriggerEnter);
      trigger.addEventListener('mouseleave', this.#onTriggerLeave);
      popover.addEventListener('mouseenter', this.#onTriggerEnter);
      popover.addEventListener('mouseleave', this.#onTriggerLeave);
    }

    // Handle responsiveness
    if (this.hasAttribute('data-close-on-resize')) {
      window.addEventListener('resize', debounce(() => {
        if (popover.matches(':popover-open')) popover.hidePopover();
      }, 150));
    }
  }

  #onTriggerEnter = () => {
    const { trigger, popover } = this.refs;
    trigger.dataset.hoverActive = 'true';
    
    if (!popover.matches(':popover-open')) {
      if (this.#popoverTrigger) clearTimeout(this.#popoverTrigger);
      this.#popoverTrigger = window.setTimeout(() => {
        if (trigger.matches('[data-hover-active]')) popover.showPopover();
      }, this.interaction_delay);
    }
  };

  #onTriggerLeave = () => {
    const { trigger, popover } = this.refs;
    delete trigger.dataset.hoverActive;

    if (this.#popoverTrigger) clearTimeout(this.#popoverTrigger);

    if (popover.matches(':popover-open')) {
      this.#popoverTrigger = window.setTimeout(() => {
        if (!trigger.matches('[data-hover-active]') && !popover.matches(':hover')) {
          popover.hidePopover();
        }
      }, this.interaction_delay);
    }
  };

  /**
   * @param {ToggleEvent} event
   */
  #onPopoverToggle = (event) => {
    if (event.newState === 'open') {
      requestIdleCallback(() => this.#calculatePosition());
    }
  };

  /**
   * Calculates the position of the trigger and sets CSS variables on the popover.
   */
  #calculatePosition() {
    const { trigger, popover } = this.refs;
    const rect = trigger.getBoundingClientRect();

    // Set custom properties for CSS consumption
    popover.style.setProperty('--anchor-top', `${rect.top + window.scrollY}px`);
    popover.style.setProperty('--anchor-left', `${rect.left + window.scrollX}px`);
    popover.style.setProperty('--anchor-width', `${rect.width}px`);
    popover.style.setProperty('--anchor-height', `${rect.height}px`);
    popover.style.setProperty('--anchor-bottom', `${rect.bottom + window.scrollY}px`);
  }
}

if (!customElements.get('anchored-popover-component')) {
  customElements.define('anchored-popover-component', AnchoredPopoverComponent);
}